import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasActiveSubscription } from '../../utils/stripeService';
import gameService from '../../utils/gameService';
import { supabase } from '../../utils/supabase';
import { parseGameParam, generateGameUrl } from '../../utils/urlUtils';
import { trackGameDiscovered, trackGameInterest, trackFirstPlay } from '../../utils/analyticsService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import GameHero from './components/GameHero';
import GameTabs from './components/GameTabs';
import RelatedGames from './components/RelatedGames';
import GameStats from './components/GameStats';
import FriendsPlaying from './components/FriendsPlaying';
import WebGPUGamePlayerV2 from '../../components/WebGPUGamePlayerV2';

const GameDetailLaunch = () => {
  const [gameData, setGameData] = useState(null);
  const [relatedGames, setRelatedGames] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [isGamePlaying, setIsGamePlaying] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  const { user } = useAuth();
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Always fetch fresh game data from database to ensure we have complete data
        let game = null;
        
        if (gameId) {
          // Parse the game parameter to determine if it's a UUID or slug
          const { type, value } = parseGameParam(gameId);
          
          let result;
          if (type === 'uuid') {
            // Use existing UUID-based lookup
            result = await gameService.getGameById(value);
          } else if (type === 'slug') {
            // Use new slug-based lookup
            result = await gameService.getGameBySlug(value);
          } else {
            setError('Invalid game identifier');
            return;
          }
          
          if (result?.success) {
            game = result.data;
            
            // If we loaded by UUID but have a title, redirect to pretty URL
            if (type === 'uuid' && game.title) {
              const prettyUrl = generateGameUrl(game);
              navigate(prettyUrl, { replace: true });
              return; // Exit early, the redirect will trigger a new load
            }
          } else {
            setError(result?.error || 'Failed to load game data');
            return;
          }
        }

        if (!game) {
          setError('Game not found');
          return;
        }

        setGameData(game);

        // Track player journey: discovered (only if user is logged in)
        if (user?.id && game?.id) {
          // Determine discovery source from URL params or referrer
          const searchParams = new URLSearchParams(location.search);
          const source = searchParams.get('source') ||
                        (document.referrer.includes('search') ? 'search' :
                         document.referrer.includes('featured') ? 'featured' : 'direct');

          trackGameDiscovered(user.id, game.id, source).catch(err =>
            console.error('Failed to track discovery:', err)
          );
        }

        // Load related games from the same category
        if (game.game_category_mappings?.length > 0) {
          const categoryName = game.game_category_mappings[0].game_categories.name;
          const relatedResult = await gameService.getGamesByCategory(categoryName, 4);
          if (relatedResult?.success) {
            // Filter out current game
            const filtered = relatedResult.data.filter(g => g.id !== game.id);
            setRelatedGames(filtered);
          }
        }

        // Load user-specific data if authenticated
        if (user?.id) {
          // Check if favorited
          const favoritesResult = await gameService.getUserFavorites(user.id);
          if (favoritesResult?.success) {
            const isFav = favoritesResult.data.some(fav => fav.game_id === game.id);
            setIsFavorite(isFav);
          }

          // Load user stats for this game
          const sessionsResult = await gameService.getUserGameSessions(user.id);
          if (sessionsResult?.success) {
            const gameSessions = sessionsResult.data.filter(session => session.game_id === game.id);
            if (gameSessions.length > 0) {
              const totalPlaytime = gameSessions.reduce((sum, session) => sum + (session.duration_minutes || 0), 0);
              const lastSession = gameSessions[0];
              const hours = Math.floor(totalPlaytime / 60);
              const minutes = totalPlaytime % 60;
              
              setUserStats({
                playtime: `${hours}h ${minutes}m`,
                lastPlayed: new Date(lastSession.created_at).toLocaleDateString(),
                progress: lastSession.progress_percentage || 0
              });
            }
          }

          // Load user review
          const reviewsResult = await gameService.getUserGameRating(user.id, game.id);
          if (reviewsResult?.success && reviewsResult.data) {
            setUserReview({
              ...reviewsResult.data,
              comment: reviewsResult.data.review_text,
              date: reviewsResult.data.created_at
            });
          }
        }
      } catch (err) {
        setError('Failed to load game data');
        console.log('Error loading game data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadGameData();
  }, [gameId, location.state, user]);

  // Handle session end tracking on page unload or component unmount
  useEffect(() => {
    const endSession = async () => {
      if (currentSessionId) {
        const { error } = await supabase
          .from('user_game_sessions')
          .update({
            session_end: new Date().toISOString()
          })
          .eq('id', currentSessionId);

        if (error) {
          // Session tracking is non-critical - silently ignore failures
        }
      }
    };

    // Handle page unload (user navigating away or closing tab)
    const handleBeforeUnload = () => {
      if (currentSessionId) {
        // Use sendBeacon for reliable tracking on page unload
        const sessionEnd = new Date().toISOString();
        navigator.sendBeacon(
          '/api/end-session',
          JSON.stringify({ sessionId: currentSessionId, sessionEnd })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession();
    };
  }, [currentSessionId]);

  const handlePlayGame = async () => {
    // Track player journey: interested (play button clicked)
    if (user?.id && gameData?.id) {
      trackGameInterest(user.id, gameData.id, 'play_clicked').catch(err =>
        console.error('Failed to track interest:', err)
      );
    }

    // For premium games, check if user is authenticated and has subscription
    if (gameData?.tier === 'premium') {
      // Check if user is authenticated
      if (!user) {
        setShowLoginModal(true);
        return;
      }

      // Check subscription status for premium games
      try {
        const hasActiveSub = await hasActiveSubscription();
        if (!hasActiveSub) {
          // Redirect to subscription page
          navigate('/subscription-management', {
            state: { 
              returnTo: `/games/${gameData.id}`,
              gameTitle: gameData.title 
            }
          });
          return;
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        navigate('/subscription-management');
        return;
      }
    }

    // For free games, no authentication required
    // For premium games, user is authenticated and has subscription at this point
    try {
      // Record game session start only if user is logged in
      if (user?.id && gameData?.id) {
        const sessionResult = await gameService.recordGameSession(user.id, gameData.id, {
          session_start: new Date().toISOString()
        });

        // Store session ID for later cleanup
        if (sessionResult.success && sessionResult.data) {
          setCurrentSessionId(sessionResult.data.id);
        }

        // Track player journey: first_play (checks if truly first play internally)
        trackFirstPlay(user.id, gameData.id).catch(err =>
          console.error('Failed to track first play:', err)
        );
      }

      // Start the game
      setIsGamePlaying(true);
    } catch (err) {
      console.log('Failed to record game session:', err);
      // Still allow game to start even if session recording fails
      setIsGamePlaying(true);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?.id || !gameData?.id) return;

    try {
      const result = await gameService.toggleFavorite(user.id, gameData.id);
      if (result?.success) {
        setIsFavorite(result.isFavorited);

        // Track player journey: interested (favorited game)
        if (result.isFavorited) {
          trackGameInterest(user.id, gameData.id, 'favorited').catch(err =>
            console.error('Failed to track interest:', err)
          );
        }
      }
    } catch (err) {
      console.log('Failed to toggle favorite:', err);
    }
  };

  const handleSubmitReview = async (review) => {
    if (!user?.id || !gameData?.id) return;

    try {
      const result = await gameService.submitGameRating(user.id, gameData.id, review);
      if (result?.success) {
        // Update user review state
        setUserReview({
          ...result.data,
          comment: result.data.review_text,
          date: result.data.created_at
        });
        
        // Reload game data to get updated reviews and rating
        const gameResult = await gameService.getGameById(gameData.id);
        if (gameResult?.success) {
          setGameData(gameResult.data);
        }
        
        return true;
      }
      return false;
    } catch (err) {
      console.log('Failed to submit review:', err);
      return false;
    }
  };

  const handleCloseGame = () => {
    setIsGamePlaying(false);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading game details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !gameData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Game not found'}</p>
            <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If game is playing, show the game player
  if (isGamePlaying && gameData) {
    return (
      <div className="fixed inset-0 z-50">
        <WebGPUGamePlayerV2
          game={gameData}
          onClose={handleCloseGame}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 pb-20 lg:pb-8">
        {/* Game Hero Section */}
        <GameHero
          game={gameData}
          onPlayGame={handlePlayGame}
          onToggleFavorite={handleToggleFavorite}
          isFavorite={isFavorite}
          userStats={userStats}
        />

        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Game Tabs */}
              <GameTabs
                game={gameData}
                userReview={userReview}
                onSubmitReview={handleSubmitReview}
              />

              {/* Related Games */}
              {relatedGames.length > 0 && (
                <RelatedGames
                  games={relatedGames}
                  title="More Like This"
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Friends Playing */}
              <FriendsPlaying gameId={gameData.id} />

              {/* Game Stats */}
              <GameStats
                game={gameData}
                userStats={userStats}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Login Modal - Show existing login modal when user is not authenticated */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="UserPlus" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Premium Game - Login Required</h3>
                  <p className="text-sm text-muted-foreground">Sign in to play this premium game: {gameData?.title}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" iconName="X" onClick={handleCloseLoginModal} />
            </div>

            <div className="space-y-3">
              <Button 
                variant="default" 
                size="sm" 
                iconName="LogIn" 
                iconPosition="left" 
                className="w-full"
                onClick={() => {
                  handleCloseLoginModal();
                  navigate('/user-login', { 
                    state: { 
                      returnTo: `/games/${gameData?.id}`,
                      gameTitle: gameData?.title 
                    } 
                  });
                }}
              >
                Sign In
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => {
                  handleCloseLoginModal();
                  navigate('/user-registration', { 
                    state: { 
                      returnTo: `/games/${gameData?.id}`,
                      gameTitle: gameData?.title,
                      tier: gameData?.tier 
                    } 
                  });
                }}
              >
                Create Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameDetailLaunch;
